import React from "react";
import { Box, Theme, Typography, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import { format, parseJSON } from "date-fns";
import {
  Booking,
  Investment,
  InvestmentTypeEnum,
  useListAndSearchAllBookings,
} from "@rentablo/api/apiv1";
import {
  getNumberOfLotsByInvestmentAndBookingType,
  getValueByInvestmentAndBookingType,
  toCurrency,
} from "@helpers";
import { BookingType } from "@components";
import { ScrollableWithGradients } from "@rentablo/components";
import { LoadingPlaceHolder } from "@rentablo/components";
import { TransactionStatusIcon } from "@features/investments/investment/components/TransactionStatusIcon.tsx";
import { RangeSelectorControlProps } from "@hooks";

const BookingRow = ({
  booking,
  investment,
}: {
  booking: Booking;
  investment: Investment;
}) => {
  const numberOfLotsText = getNumberOfLotsByInvestmentAndBookingType({
    value: booking.numberOfLots,
    investmentType: booking.investment?.type as InvestmentTypeEnum,
    bookingType: booking.type,
  });
  const priceText = getValueByInvestmentAndBookingType({
    value: Math.abs(booking.securityPrice),
    investmentType: booking.investment?.type as InvestmentTypeEnum,
    bookingType: booking.type,
    currency: booking.currency,
    percentageRange: "0-100",
    quoteNotation: investment.quoteNotation,
  });
  const subtitle = numberOfLotsText
    ? `${numberOfLotsText} ${priceText}`
    : priceText;

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      py={2}
      px={5}
      sx={{
        borderBottom: (theme) => `1px solid ${theme.palette.border.secondary}`,
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Box>
        <BookingType type={booking.type} />
        <Box display="flex" gap={1} alignItems="center">
          <TransactionStatusIcon
            dataConsistencyStatus={booking.dataConsistencyStatus}
            dataSource={booking.dataSource}
            isExternalID={Boolean(booking.externalId)}
          />
          <Typography variant="body3" color="textSecondary" mt={0.5}>
            {format(parseJSON(booking.valueDate), "dd.MM.yyyy")}
          </Typography>
        </Box>
      </Box>
      <Box textAlign="right">
        <Typography
          variant="body2"
          fontWeight={600}
          color={booking.amount >= 0 ? "text.positive" : "text.negative"}
        >
          {toCurrency(booking.amount, booking.currency)}
        </Typography>
        <Typography variant="caption2" color="textSecondary" display="block">
          {subtitle}
        </Typography>
      </Box>
    </Box>
  );
};

export const ProtocolTab = ({
  investment,
  displayCurrency,
  rangeProps,
}: {
  investment: Investment;
  displayCurrency: string;
  rangeProps?: RangeSelectorControlProps;
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const isLessMd = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );
  const { data, isFetching } = useListAndSearchAllBookings({
    investmentId: [investment.id],
    order: ["valueDate desc"],
    displayCurrency,
    minDate: rangeProps?.range.startDate.toISOString(),
    maxDate: rangeProps?.range.endDate.toISOString(),
  });

  if (isFetching)
    return (
      <Box mt={4} px={6}>
        <LoadingPlaceHolder
          height={isMobile ? "320px" : isLessMd ? "320px" : "296px"}
        />
      </Box>
    );

  const bookings = data?.bookings ?? [];

  if (bookings.length === 0) {
    return (
      <Box py={4} textAlign="center">
        <Typography variant="body3" color="textSecondary">
          {t("domains.investments.investmentTable.detailModal.noBookings")}
        </Typography>
      </Box>
    );
  }

  return (
    <ScrollableWithGradients
      maxHeight={{ xs: 336, md: 312 }}
      minHeight={isMobile ? 336 : 312}
    >
      {bookings.map((booking) => (
        <BookingRow
          key={booking.id}
          booking={booking}
          investment={investment}
        />
      ))}
    </ScrollableWithGradients>
  );
};
